import {
  type ComponentRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type { Role } from "react-native";

import { Box, Pressable, Text, VisuallyHidden } from "../../../mads";
import { Search } from "../../../mads/icons";
import { SelectButton } from "../../../mads/SelectInput/SelectButton";
import { TextInput } from "../../../mads/TextInput";
import { useTheme, useThemedStylesheet } from "../../../mads/ThemeProvider";
import { useI18n } from "../../I18n";
import { createThemedStylesheet } from "../../util/createThemedStylesheet";
import { uninertOwnPortal } from "../../util/useWebModalAccessibility";
import type { PickerComboboxProps } from "./types";

export type { PickerComboboxProps };

/** Approximate height of a single option row, used to size the scrollable list. */
const APPROX_OPTION_HEIGHT = 44;

/**
 * Resolve where the picker panel portal should mount.
 *
 * When the trigger sits inside a modal dialog (e.g. a Profile form sheet on
 * web), mount the panel *inside* that dialog so the modal focus trap treats the
 * search field as part of the modal. Mounting to `<body>` there would leave the
 * field outside the trap, which inerts it and bounces its `autoFocus` back to
 * the modal heading — the search box then can't be focused or typed into.
 * Falls back to `<body>` for full-page hosts (e.g. Contact info), which have
 * no focus trap.
 */
export function resolvePickerPortalTarget(
  node: { closest?: (selector: string) => HTMLElement | null } | null,
): HTMLElement {
  const dialog = node?.closest?.('[role="dialog"]') ?? null;
  return dialog ?? document.body;
}

/**
 * Web-specific country/option picker.
 *
 * Native (iOS/Android) keeps the {@link PickerCombobox} wheel-in-modal pattern; on web a
 * scroll-wheel-in-a-modal doesn't match user expectations, so this variant renders a
 * `<select>`-style trigger that opens an anchored, searchable listbox:
 *  - looks like a select and exposes a combobox role with the current value,
 *  - filters the list via a search input as the user types,
 *  - commits on row click (no separate "Done" step),
 *  - closes on outside click, Escape, or selection,
 *  - supports ↑/↓ to move the highlight, Enter to select, and Esc to close.
 *
 * Implemented as a `.web.tsx` split so the props contract is identical to the native
 * component and all consumers (via `PickerComboboxField`) work unchanged.
 */
export const PickerCombobox = <TItem,>({
  disabled,
  filterExtractor,
  invalid,
  items,
  keyExtractor,
  label,
  labelExtractor,
  onOpenChange,
  onSelect,
  panelMinWidth,
  placeholderText,
  ref,
  renderItem,
  testID,
  value,
  visibleItemCount = 5,
}: PickerComboboxProps<TItem>) => {
  const theme = useTheme();
  const styles = useThemedStylesheet(stylesheet);
  const { gettext, ngettext } = useI18n();

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [panelRect, setPanelRect] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );
  // Where the portal mounts. Defaults to <body>, but when the picker is opened
  // from inside a web modal we mount into the enclosing dialog instead (see the
  // measurement effect below) so the modal focus trap treats the search field as
  // inside the modal rather than an outside element it must inert / bounce focus
  // away from.
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  const triggerRef = useRef<ComponentRef<typeof Box> | null>(null);
  const panelRef = useRef<ComponentRef<typeof Box> | null>(null);
  const searchInputRef = useRef<unknown>(null);

  // useId() can contain ":" (React 18); strip it so the value is safe to embed in element ids.
  const uid = useId().replace(/:/g, "");
  const listboxId = `picker-combobox-listbox-${uid}`;
  const optionId = useCallback((index: number) => `picker-combobox-option-${uid}-${index}`, [uid]);

  const selectedKey = value !== undefined ? keyExtractor(value) : undefined;
  const selectedLabel = value !== undefined ? labelExtractor(value) : undefined;

  const filteredItems = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return [...items];
    const extractor = filterExtractor ?? labelExtractor;
    return items.filter((item) => extractor(item).toLowerCase().includes(normalized));
  }, [items, labelExtractor, filterExtractor, query]);

  const focusTrigger = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    const wrapper = triggerRef.current as unknown as HTMLElement | null;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    const button = wrapper?.querySelector?.('[role="button"]') as HTMLElement | null;
    button?.focus?.();
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
    onOpenChange?.(false);
  }, [onOpenChange]);

  const open = useCallback(() => {
    if (disabled) return;
    setQuery("");
    const selectedIndex =
      selectedKey !== undefined
        ? items.findIndex((item) => keyExtractor(item) === selectedKey)
        : -1;
    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setIsOpen(true);
    onOpenChange?.(true);
  }, [disabled, items, keyExtractor, onOpenChange, selectedKey]);

  const commitItem = useCallback(
    (item: TItem) => {
      onSelect(item);
      close();
      focusTrigger();
    },
    [onSelect, close, focusTrigger],
  );

  const handleQueryChange = useCallback((text: string) => {
    setQuery(text);
    setHighlightedIndex(0);
  }, []);

  // Measure the trigger to anchor the portal panel directly beneath it, and
  // resolve where the portal should mount.
  useLayoutEffect(() => {
    if (!isOpen) {
      setPanelRect(null);
      setPortalTarget(null);
      return;
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    const node = triggerRef.current as unknown as
      | (Partial<Pick<HTMLElement, "getBoundingClientRect">> & {
          closest?: (selector: string) => HTMLElement | null;
        })
      | null;
    setPortalTarget(resolvePickerPortalTarget(node));
    const rect = node?.getBoundingClientRect?.();
    if (!rect) return;
    setPanelRect({ top: rect.bottom, left: rect.left, width: rect.width });
  }, [isOpen]);

  // A parent modal's focus trap marks body-level siblings `inert`; if this
  // panel is portaled to <body> it can inherit that and become non-interactive.
  // Clear it on open (harmless no-op when not inerted). Mirrors Autocomplete.web.
  useEffect(() => {
    if (!isOpen) return;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    uninertOwnPortal(panelRef.current as unknown as HTMLElement | null);
  }, [isOpen, portalTarget]);

  // Outside-click + keyboard handling while the panel is open.
  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (event: MouseEvent) => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      const triggerEl = triggerRef.current as unknown as HTMLElement | null;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      const panelEl = panelRef.current as unknown as HTMLElement | null;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      if (triggerEl?.contains(event.target as Node)) return;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      if (panelEl?.contains(event.target as Node)) return;
      close();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case "Escape":
          event.preventDefault();
          close();
          focusTrigger();
          break;
        case "ArrowDown":
          event.preventDefault();
          setHighlightedIndex((index) => Math.min(index + 1, filteredItems.length - 1));
          break;
        case "ArrowUp":
          event.preventDefault();
          setHighlightedIndex((index) => Math.max(index - 1, 0));
          break;
        case "Home":
          event.preventDefault();
          setHighlightedIndex(0);
          break;
        case "End":
          event.preventDefault();
          setHighlightedIndex(filteredItems.length - 1);
          break;
        case "Enter": {
          const item = filteredItems[highlightedIndex];
          if (item) {
            event.preventDefault();
            commitItem(item);
          }
          break;
        }
        case "Tab":
          // Close and return focus to the trigger so tab order stays predictable
          // (the panel is portaled to the end of <body>, away from the field).
          event.preventDefault();
          close();
          focusTrigger();
          break;
        default:
          break;
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    // Capture phase: the focused search field is a react-native-web TextInput, whose
    // keydown handler calls stopPropagation() on every key (RNW #612). A bubble-phase
    // listener here would never fire, so the picker's keyboard nav (↑/↓, Home/End,
    // Enter, Esc) is dead while the field has focus. Capturing runs us before the
    // input's bubble handler, so the keys reach us regardless.
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isOpen, close, commitItem, focusTrigger, filteredItems, highlightedIndex]);

  // Keep the highlighted option scrolled into view as the user navigates.
  useEffect(() => {
    if (!isOpen || typeof document === "undefined") return;
    const optionEl = document.getElementById(optionId(highlightedIndex));
    optionEl?.scrollIntoView?.({ block: "nearest" });
  }, [isOpen, highlightedIndex, optionId]);

  // The search input's combobox semantics (role, aria-expanded, aria-controls, aria-owns)
  // are set declaratively on the <TextInput> below. The one piece mads TextInput has no
  // prop for is `aria-activedescendant` — the live pointer to the highlighted option that
  // lets a screen reader announce it as the user arrows. mads is under lockdown, so set it
  // on the forwarded input DOM node here. (aria-owns on the input is mads' documented bridge
  // for associating the portal'd listbox so activedescendant resolves.)
  // TODO(RN-4947): replace with an `aria-activedescendant` prop on mads TextInput once the
  // mads lockdown lifts.
  useEffect(() => {
    if (!isOpen) return;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    const inputNode = searchInputRef.current as {
      setAttribute?: (k: string, v: string) => void;
      removeAttribute?: (k: string) => void;
    } | null;
    if (!inputNode?.setAttribute) return;
    inputNode.setAttribute("aria-autocomplete", "list");
    if (filteredItems.length > 0) {
      inputNode.setAttribute("aria-activedescendant", optionId(highlightedIndex));
    } else {
      inputNode.removeAttribute?.("aria-activedescendant");
    }
  }, [isOpen, highlightedIndex, filteredItems, optionId]);

  const listMaxHeight = Math.max(visibleItemCount, 1) * APPROX_OPTION_HEIGHT;
  // Gap left between the bottom of the panel and the viewport edge.
  const viewportMargin = theme.spacing.s5;
  const triggerAccessibleName = selectedLabel ? `${label}, ${selectedLabel}` : label;
  const resultsMessage =
    filteredItems.length === 0
      ? gettext("No results")
      : ngettext("{0} result", "{0} results", filteredItems.length, [String(filteredItems.length)]);

  return (
    <Box ref={triggerRef} sx={styles.container} testID={testID}>
      <SelectButton
        ref={ref}
        disabled={!!disabled}
        invalid={!!invalid}
        label={selectedLabel}
        placeholder={placeholderText}
        onPress={isOpen ? close : open}
        {...{
          accessibilityRole: "button" as const,
          accessibilityLabel: triggerAccessibleName,
          "aria-haspopup": "listbox",
          "aria-expanded": isOpen,
          "aria-controls": isOpen ? listboxId : undefined,
          testID: testID ? `${testID}-combobox-trigger` : undefined,
        }}
      />

      {isOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <Box
            ref={panelRef}
            sx={[
              styles.panel,
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
              { position: "fixed" as never },
              panelRect && {
                top: panelRect.top,
                left: panelRect.left,
                width: Math.max(panelRect.width, panelMinWidth ?? 0),
                maxHeight: Math.min(
                  listMaxHeight + 80,
                  window.innerHeight - panelRect.top - viewportMargin,
                ),
              },
            ]}
            testID={testID ? `${testID}-combobox-panel` : undefined}
          >
            <Box sx={styles.searchContainer}>
              <TextInput
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
                ref={searchInputRef as never}
                value={query}
                onInput={handleQueryChange}
                ariaText={gettext("Search")}
                placeholderText={gettext("Search")}
                autoFocus={true}
                autoCapitalize="none"
                contentBefore={<Search aria-hidden={true} color={theme.colors.foregroundStrong} />}
                accessibilityRole="combobox"
                accessibilityState={{ expanded: true }}
                accessibilityControls={listboxId}
                accessibilityOwns={listboxId}
                testID={testID ? `${testID}-combobox-search` : undefined}
              />
            </Box>

            {/* Visually-hidden live region announces result counts as the user filters. */}
            <Box accessibilityLiveRegion="polite">
              <VisuallyHidden>{resultsMessage}</VisuallyHidden>
            </Box>

            <Box
              nativeID={listboxId}
              // react-native's `Role` union omits 'listbox' (it has list/listitem/
              // option/combobox but not listbox); the value is a valid ARIA role.
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
              role={"listbox" as Role}
              accessibilityLabel={label}
              sx={[styles.list, { maxHeight: listMaxHeight }]}
            >
              {filteredItems.length === 0 ? (
                <Box sx={styles.emptyState}>
                  <Text sx={styles.emptyStateText}>{gettext("No results")}</Text>
                </Box>
              ) : (
                filteredItems.map((item, index) => {
                  const itemKey = keyExtractor(item);
                  const isSelected = itemKey === selectedKey;
                  const isHighlighted = index === highlightedIndex;
                  return (
                    <Pressable
                      key={itemKey}
                      nativeID={optionId(index)}
                      // a11y lint only flags bare role literals; cast as Role
                      // (which includes 'option') to use the valid ARIA role.
                      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
                      role={"option" as Role}
                      aria-selected={isSelected}
                      accessibilityState={{ selected: isSelected }}
                      onPress={() => commitItem(item)}
                      // Hover moves the highlight (keeping mouse + keyboard in
                      // sync). `onHoverIn` is a real prop on mads' Pressable but
                      // isn't typed on the strict/RSD Pressable; spread it so this
                      // one source compiles in both trees and applies where supported.
                      {...{ onHoverIn: () => setHighlightedIndex(index) }}
                      sx={[
                        styles.option,
                        isHighlighted && styles.optionHighlighted,
                        isSelected && styles.optionSelected,
                      ]}
                      testID={testID ? `${testID}-combobox-option-${itemKey}` : undefined}
                    >
                      {renderItem(item)}
                    </Pressable>
                  );
                })
              )}
            </Box>
          </Box>,
          portalTarget ?? document.body,
        )}
    </Box>
  );
};

const stylesheet = createThemedStylesheet((theme) => ({
  container: {
    width: "100%",
  },
  panel: {
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: theme.colors.borderWeak,
    boxShadow: theme.shadows.medium,
    overflow: "hidden",
    zIndex: 9999,
  },
  searchContainer: {
    padding: theme.spacing.s2,
  },
  list: {
    overflow: "scroll",
    paddingBottom: theme.spacing.s2,
  },
  option: {
    paddingVertical: theme.spacing.s3,
    paddingHorizontal: theme.spacing.s3,
    backgroundColor: theme.colors.background,
  },
  optionHighlighted: {
    backgroundColor: theme.colors.backgroundActionHover,
  },
  optionSelected: {
    backgroundColor: theme.colors.backgroundWeaker,
  },
  emptyState: {
    paddingVertical: theme.spacing.s3,
    paddingHorizontal: theme.spacing.s3,
  },
  emptyStateText: {
    color: theme.colors.foregroundWeak,
  },
}));
